import React from 'react';

/**
 * Inline sparkline showing code-quality coverage_pct trend over recent scans.
 *
 * Each datapoint becomes a dot — green if overall_passed, red otherwise —
 * connected by a coverage line. Width auto-fits container; height fixed.
 */
export function CoverageSparkline({ points }) {
  if (!points || points.length < 2) return null;

  const W = 240;
  const H = 36;
  const pad = 4;

  const coverages = points.map(p => p.coverage_pct ?? 0);
  const maxC = Math.max(...coverages, 100);
  const minC = Math.min(...coverages, Math.max(maxC - 10, 0));
  const range = Math.max(maxC - minC, 1);

  const x = (i) => pad + (i * (W - 2 * pad)) / Math.max(points.length - 1, 1);
  const y = (c) => pad + (1 - (c - minC) / range) * (H - 2 * pad);

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.coverage_pct ?? 0).toFixed(1)}`).join(' ');

  // Area fill below the line for visual depth
  const areaPath = `${linePath} L ${x(points.length - 1).toFixed(1)} ${H - pad} L ${x(0).toFixed(1)} ${H - pad} Z`;

  const last = points[points.length - 1];
  const trendingUp = points.length >= 2 && (last.coverage_pct ?? 0) >= (points[0].coverage_pct ?? 0);
  const lineColor = trendingUp ? '#10b981' : '#f59e0b';
  const fillColor = trendingUp ? 'rgba(16,185,129,0.10)' : 'rgba(245,158,11,0.10)';

  return (
    <div className="flex items-center gap-2" data-testid="build-health-sparkline">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
        <path d={areaPath} fill={fillColor} />
        <path d={linePath} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => {
          const dotColor = p.overall_passed ? '#10b981' : '#ef4444';
          const isLast = i === points.length - 1;
          const r = isLast ? 3 : 2;
          return (
            <circle
              key={i}
              cx={x(i)}
              cy={y(p.coverage_pct ?? 0)}
              r={r}
              fill={dotColor}
              stroke="white"
              strokeWidth={isLast ? 1.5 : 1}
            >
              <title>{`${new Date(p.timestamp).toLocaleString()} — ${p.coverage_pct}% type hints, ${p.overall_passed ? 'PASS' : 'FAIL'}`}</title>
            </circle>
          );
        })}
      </svg>
      <div className="flex flex-col text-[10px] leading-tight">
        <span className="text-slate-400 uppercase tracking-[0.15em] font-medium">Trend ({points.length} scans)</span>
        <span className={trendingUp ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>
          {trendingUp ? '↗ Improving' : '↘ Declining'} · {(last.coverage_pct ?? 0).toFixed(1)}%
        </span>
      </div>
    </div>
  );
}
